import React from 'react';
import styled from 'styled-components';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CircularProgress from '@mui/material/CircularProgress';

export type StepStatus = 'pending' | 'active' | 'in-progress' | 'completed' | 'warning';

export interface Step {
  name: string;
  status?: StepStatus;
}

export interface StepIndicatorProps {
  steps: Step[];
  activeStepIndex?: number; // 0-based index of the active step
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, activeStepIndex }) => {
  return (
    <Container>
      {steps.map((step, index) => {
        const isLastStep = index === steps.length - 1;
        const inferredStatus: StepStatus = activeStepIndex === undefined
          ? 'pending'
          : index < activeStepIndex || (index === activeStepIndex && isLastStep)
            ? 'completed'
            : index === activeStepIndex
              ? 'active'
              : 'pending';
        const status = step.status || inferredStatus;
        const isCompleted = status === 'completed';
        const isActive = status === 'active';
        const isInProgress = status === 'in-progress';
        const isPending = status === 'pending';
        const hasWarning = status === 'warning';

        return (
          <React.Fragment key={index}>
            <StepWrapper>
              <StepCircle
                isCompleted={isCompleted}
                isActive={isActive}
                isInProgress={isInProgress}
                isPending={isPending}
                hasWarning={hasWarning}
              >
                {isCompleted ? (
                  <CheckCircleIcon style={{ color: '#4caf50', fontSize: '22px' }} />
                ) : hasWarning ? (
                  <WarningAmberIcon style={{ color: '#ed6c02', fontSize: '22px' }} />
                ) : isInProgress ? (
                  <CircularProgress size={18} aria-label={`${step.name} in progress`} />
                ) : (
                  <StepNumber isActive={isActive} isPending={isPending}>{index + 1}</StepNumber>
                )}
              </StepCircle>
              <StepName
                isCompleted={isCompleted}
                isActive={isActive}
                isInProgress={isInProgress}
                isPending={isPending}
                hasWarning={hasWarning}
              >
                {step.name}
              </StepName>
            </StepWrapper>
            {index < steps.length - 1 && (
              <ConnectorLine />
            )}
          </React.Fragment>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const StepCircle = styled.div<{
  isCompleted: boolean;
  isActive: boolean;
  isInProgress: boolean;
  isPending: boolean;
  hasWarning: boolean;
}>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => {
    if (props.isCompleted) return 'transparent';
    if (props.isInProgress) return 'transparent';
    if (props.hasWarning) return 'transparent';
    if (props.isActive) return '#E3F2FD';
    return '#F5F5F5';
  }};
  border: 2px solid ${props => {
    if (props.isCompleted) return 'transparent';
    if (props.isInProgress) return 'transparent';
    if (props.hasWarning) return 'transparent';
    if (props.isActive) return '#2196F3';
    return '#E0E0E0';
  }};
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
`;

const StepNumber = styled.span<{ isActive: boolean; isPending: boolean }>`
  font-size: 13px;
  font-weight: bold;
  color: ${props => {
    if (props.isActive) return '#2196F3';
    if (props.isPending) return '#9E9E9E';
    return '#4caf50';
  }};
`;

const StepName = styled.div<{
  isCompleted: boolean;
  isActive: boolean;
  isInProgress: boolean;
  isPending: boolean;
  hasWarning: boolean;
}>`
  font-size: 12px;
  font-weight: ${props => props.isActive || props.isInProgress || props.isCompleted || props.hasWarning ? 'bold' : 'normal'};
  color: ${props => {
    if (props.isCompleted) return '#4caf50';
    if (props.hasWarning) return '#ed6c02';
    if (props.isInProgress) return '#2196F3';
    if (props.isActive) return '#2196F3';
    return '#9E9E9E';
  }};
  text-align: center;
  white-space: nowrap;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConnectorLine = styled.div`
  flex: 1;
  height: 1px;
  background-color: #E0E0E0;
  margin: -16px -16px 0;
  min-width: 20px;
`;

export default StepIndicator;

